import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import authService from '../services/auth/authService';
import {
  requestRefresh,
  subscribeRefreshSuccess,
  getConnectionState,
  resetConnectionState,
  subscribeConnection,
  isServerSideFailure,
  assertNever,
} from '../services/auth/refreshCoordinator';
import { requestSmsReauth, watchOtherTabs } from '../services/auth/reauthBus';
import {
  AuthState,
  LoginPayload,
  RegisterPayload,
  AuthActionPayload,
  LoginSuccessPayload,
  RegisterSuccessPayload,
  RefreshTokenSuccessPayload,
  SetUserPayload,
  ErrorPayload,
  // User, // 사용하지 않으므로 주석 처리 또는 삭제
} from '../types/auth';

// 인증 컨텍스트 기본 상태
const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  sessionExpiry: null,
};

// 세션 관련 상수
const SESSION_TIMEOUT_WARNING = 5 * 60 * 1000; // 세션 만료 5분 전에 경고
const SESSION_CHECK_INTERVAL = 60 * 1000; // 1분마다 세션 상태 확인

// 액션 타입 정의
export enum AuthActionType {
  LOGIN_REQUEST = 'LOGIN_REQUEST',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILURE = 'LOGIN_FAILURE',
  REGISTER_REQUEST = 'REGISTER_REQUEST',
  REGISTER_SUCCESS = 'REGISTER_SUCCESS',
  REGISTER_FAILURE = 'REGISTER_FAILURE',
  LOGOUT = 'LOGOUT',
  SET_USER = 'SET_USER',
  CLEAR_ERROR = 'CLEAR_ERROR',
  REFRESH_TOKEN_SUCCESS = 'REFRESH_TOKEN_SUCCESS',
  REFRESH_TOKEN_FAILURE = 'REFRESH_TOKEN_FAILURE',
  SESSION_TIMEOUT_WARNING = 'SESSION_TIMEOUT_WARNING',
  UPDATE_SESSION_EXPIRY = 'UPDATE_SESSION_EXPIRY',
}

// 액션 인터페이스 정의
interface AuthAction {
  type: AuthActionType;
  payload?: AuthActionPayload;
}

// 리듀서 함수
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case AuthActionType.LOGIN_REQUEST:
    case AuthActionType.REGISTER_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case AuthActionType.LOGIN_SUCCESS:
    case AuthActionType.REGISTER_SUCCESS: {
      if (
        action.payload &&
        typeof action.payload === 'object' &&
        'user' in action.payload &&
        'token' in action.payload
      ) {
        const payload = action.payload as LoginSuccessPayload | RegisterSuccessPayload;
        return {
          ...state,
          isLoading: false,
          isAuthenticated: true,
          user: payload.user,
          token: payload.token,
          refreshToken: payload.refreshToken || null,
          sessionExpiry: payload.sessionExpiry || null,
          error: null,
        };
      }
      return state;
    }
    case AuthActionType.LOGIN_FAILURE:
    case AuthActionType.REGISTER_FAILURE:
    case AuthActionType.REFRESH_TOKEN_FAILURE: {
      let errorMessage = '알 수 없는 오류';
      if (typeof action.payload === 'string') {
        errorMessage = action.payload;
      } else if (
        action.payload &&
        typeof action.payload === 'object' &&
        'message' in action.payload &&
        typeof action.payload.message === 'string'
      ) {
        errorMessage = action.payload.message;
      }
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        sessionExpiry: null,
        error: errorMessage,
      };
    }
    case AuthActionType.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        sessionExpiry: null,
        error: null,
      };
    case AuthActionType.SET_USER: {
      if (action.payload && typeof action.payload === 'object' && 'user' in action.payload) {
        const payload = action.payload as SetUserPayload;
        return {
          ...state,
          isLoading: false,
          isAuthenticated: true,
          user: payload.user,
          token: payload.token || state.token,
          refreshToken: payload.refreshToken || state.refreshToken,
          sessionExpiry: payload.sessionExpiry || null,
        };
      }
      return state;
    }
    case AuthActionType.REFRESH_TOKEN_SUCCESS: {
      if (action.payload && typeof action.payload === 'object' && 'token' in action.payload) {
        const payload = action.payload as RefreshTokenSuccessPayload;
        return {
          ...state,
          token: payload.token,
          refreshToken: payload.refreshToken || state.refreshToken,
          sessionExpiry: payload.sessionExpiry || null,
          isAuthenticated: true,  // ✅ 토큰 갱신 성공 시 인증 상태 true로 설정
          isLoading: false,       // ✅ 토큰 갱신 작업 완료, 로딩 상태 해제
        };
      }
      return state;
    }
    case AuthActionType.UPDATE_SESSION_EXPIRY: {
      if (
        action.payload &&
        typeof action.payload === 'object' &&
        'sessionExpiry' in action.payload &&
        action.payload.sessionExpiry instanceof Date
      ) {
        return {
          ...state,
          sessionExpiry: action.payload.sessionExpiry,
        };
      } else if (action.payload instanceof Date) {
        return {
          ...state,
          sessionExpiry: action.payload,
        };
      }
      return state;
    }
    case AuthActionType.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// 인증 컨텍스트 인터페이스 정의
interface AuthContextProps {
  state: AuthState;
  login: (credentials: LoginPayload) => Promise<void>;
  register: (userData: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  extendSession: () => Promise<void>;
  setSmsAuthSuccess: (response: LoginSuccessPayload) => void; // SMS 인증 성공 시 직접 상태 업데이트
}

// 인증 컨텍스트 생성 - Storybook에서 사용할 수 있도록 export
export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

// 인증 컨텍스트 제공자 인터페이스
interface AuthProviderProps {
  children: ReactNode;
}

// 인증 컨텍스트 제공자 컴포넌트
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionWarningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /** 만료 시각에 정확히 한 번 울리는 타이머 (60초 순회의 구멍을 메운다). */
  const sessionExpiryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /** 만료 처리가 도는 중인지. ⛔ 없으면 같은 만료에 재발급이 여러 번 나간다. */
  const 만료처리중Ref = useRef(false);

  // 로그인 함수
  const login = async (credentials: LoginPayload): Promise<void> => {
    dispatch({ type: AuthActionType.LOGIN_REQUEST });
    try {
      const response = await authService.login(credentials);

      // 휴대폰 인증이 필요한 경우
      if ((response as any).phone_verification_required) {
        console.error('AuthContext.login: 휴대폰 인증 필요');
        throw new Error('phone_verification_required: 휴대폰 인증이 필요합니다.');
      }

      if (!response.success) {
        console.error('AuthContext.login: 로그인 응답 실패');
        throw new Error(response.message || '로그인에 실패했습니다.');
      }

      if (!response.token) {
        console.error('AuthContext.login: 응답에 토큰이 없음');
        throw new Error('인증 토큰이 제공되지 않았습니다.');
      }

      // 세션 만료 시간 계산 (기본 1시간)
      const expiresIn = response.expiresIn || 3600; // 기본 1시간
      const sessionExpiry = new Date(Date.now() + expiresIn * 1000);

      dispatch({
        type: AuthActionType.LOGIN_SUCCESS,
        payload: {
          user: response.user,
          token: response.token,
          refreshToken: response.refreshToken,
          sessionExpiry,
        } as LoginSuccessPayload,
      });

      // 세션 타임아웃 모니터링 시작
      startSessionTimeoutMonitoring(sessionExpiry);
    } catch (error) {
      console.error('AuthContext.login: 로그인 실패', error);
      let errorMessage = '로그인 실패';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      dispatch({
        type: AuthActionType.LOGIN_FAILURE,
        payload: errorMessage as ErrorPayload,
      });
      throw error;
    }
  };

  // 회원가입 함수
  const register = async (userData: RegisterPayload): Promise<void> => {
    dispatch({ type: AuthActionType.REGISTER_REQUEST });
    try {
      const response = await authService.register(userData);

      // ── 관리자 승인 대기 [자체심사 2026-08-21] ──────────────────────────
      //
      // ⛔ 승인 대기면 서버가 **토큰을 주지 않는다.** 그런데 예전에는 그 응답도
      //    REGISTER_SUCCESS 로 흘려보내, 토큰이 undefined 인 채 «로그인 상태» 로 만들었다.
      //    사용자는 「가입 완료」를 보고 화면에 들어가려다 곧바로 401 로 튕긴다.
      //    승인 대기는 «실패» 가 아니라 «다음 단계가 있는 성공» 이므로 따로 알린다.
      if ((response as { pendingApproval?: boolean }).pendingApproval) {
        dispatch({
          type: AuthActionType.REGISTER_FAILURE,
          payload: (response.message
            || '회원가입이 접수되었습니다. 관리자 승인 후 로그인할 수 있습니다.') as ErrorPayload,
        });
        return;
      }

      // 세션 만료 시간 계산 (기본 1시간)
      const expiresIn = response.expiresIn || 3600; // 기본 1시간
      const sessionExpiry = new Date(Date.now() + expiresIn * 1000);

      dispatch({
        type: AuthActionType.REGISTER_SUCCESS,
        payload: {
          user: response.user,
          token: response.token,
          refreshToken: response.refreshToken,
          sessionExpiry,
        } as RegisterSuccessPayload,
      });

      // 세션 타임아웃 모니터링 시작
      startSessionTimeoutMonitoring(sessionExpiry);
    } catch (error) {
      console.error('AuthContext: 회원가입 실패', error);
      let errorMessage = '회원가입 실패';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      dispatch({
        type: AuthActionType.REGISTER_FAILURE,
        payload: errorMessage as ErrorPayload,
      });
      throw error;
    }
  };

  // 세션 타임아웃 정리
  const clearSessionTimeouts = useCallback(() => {
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
      sessionCheckIntervalRef.current = null;
    }

    if (sessionWarningTimeoutRef.current) {
      clearTimeout(sessionWarningTimeoutRef.current);
      sessionWarningTimeoutRef.current = null;
    }

    if (sessionExpiryTimeoutRef.current) {
      clearTimeout(sessionExpiryTimeoutRef.current);
      sessionExpiryTimeoutRef.current = null;
    }
  }, []);

  // 로그아웃 함수
  const logout = useCallback(async (): Promise<void> => {
    clearSessionTimeouts();
    // ⛔ 조정자가 «포기» 상태로 멈춰 있으면 그대로 두면 안 된다 — 최종 심사 [F-3].
    //    풀지 않으면 **다시 로그인해도** 이후 재발급이 전부 막힌다.
    resetConnectionState();
    await authService.logout();
    dispatch({ type: AuthActionType.LOGOUT });
  }, [clearSessionTimeouts, dispatch]);

  // 세션 상태 확인
  const checkSessionStatus = useCallback(
    (expiryDate: Date) => {
      if (!expiryDate) return;

      const now = new Date();
      const timeRemaining = expiryDate.getTime() - now.getTime();

      // 세션 만료 5분 전
      if (timeRemaining <= SESSION_TIMEOUT_WARNING && timeRemaining > 0) {
        // 세션 만료 경고 표시 (UI에서 처리 가능)
        console.warn('세션이 곧 만료됩니다.');
      }

      // ── 세션 만료 ────────────────────────────────────────────────
      //
      // ⛔ 종전에는 여기서 곧바로 `logout()` 을 불렀다 [부당한 로그아웃 제거 1단계].
      //    재발급권이 살아 있는데도 버렸고, `logout()` 이 그것까지 지웠다.
      //    이제는 **재발급을 먼저 시도**하고, 재발급권이 정말 무효일 때만 로그아웃한다.
      if (timeRemaining <= 0) {
        // ⛔ 재진입 방지. 이 함수는 1분 순회 + 만료 타이머 양쪽에서 불린다.
        //    막지 않으면 같은 만료에 대해 재발급이 여러 번 나간다.
        if (만료처리중Ref.current) return;
        만료처리중Ref.current = true;

        void (async () => {
          try {
            const outcome = await requestRefresh();

            // ⛔ `switch` + `assertNever` — `if` 사슬이면 갈래가 늘어도 타입 검사가
            //    막지 못하고 조용히 로그아웃으로 떨어진다 (Codex [C-1]).
            switch (outcome.kind) {
              case 'ok':
                return;   // 성공 — 구독자가 새 시각으로 다시 건다

              case 'unavailable':
                // ⛔ 서버 사정이다. **로그아웃하지 않는다.**
                //    조정자가 뒤에서 다시 시도한다. 타이머만 멈춰 되돌이를 막는다.
                clearSessionTimeouts();
                return;

              case 'sms_required':
                // ⛔ **로그아웃하지 않는다.** 전역 재인증 창을 띄우고 타이머만 멈춘다.
                requestSmsReauth({ challenge: outcome.challenge, phoneNumber: outcome.phoneNumber });
                clearSessionTimeouts();
                return;

              case 'blocked':
                if (typeof window !== 'undefined') {
                  const 이미로그인화면 = window.location?.pathname === '/login';
                  if (!이미로그인화면) {
                    try { window.sessionStorage.setItem('auth_blocked_reason', outcome.reason); } catch { /* noop */ }
                  }
                }
                break;

              case 'invalid':
                break;

              default:
                assertNever(outcome);
            }

            await logout();
          } finally {
            만료처리중Ref.current = false;
          }
        })();
      }
    },
    [logout, clearSessionTimeouts],
  );

  // 세션 타임아웃 모니터링 시작
  const startSessionTimeoutMonitoring = useCallback(
    (expiryDate: Date) => {
      // 기존 타이머 정리
      clearSessionTimeouts();

      if (!expiryDate) return;

      // 세션 체크 인터벌 설정
      //
      // ⛔ 순회 간격이 60초라, 이것만으로는 만료를 **최대 60초 늦게** 알아챈다.
      //    종전에는 `SessionTimeoutAlert` 가 1초마다 세면서 자기가 로그아웃시켜
      //    그 구멍을 가리고 있었다. 그 로그아웃을 걷어냈으므로(4-A),
      //    **만료 시각에 정확히 한 번 울리는 타이머**를 여기서 직접 건다.
      //    순회는 보조로 남긴다 — 컴퓨터가 절전에서 깨면 타이머가 늦게 울린다.
      sessionCheckIntervalRef.current = setInterval(() => {
        checkSessionStatus(expiryDate);
      }, SESSION_CHECK_INTERVAL);

      const 만료까지 = expiryDate.getTime() - Date.now();
      if (만료까지 > 0) {
        sessionExpiryTimeoutRef.current = setTimeout(() => {
          checkSessionStatus(expiryDate);
        }, 만료까지 + 200);   // 200ms 여유 — 경계에서 timeRemaining 이 1ms 남는 것을 피한다
      } else {
        checkSessionStatus(expiryDate);
      }

      // 세션 만료 경고 타이머 설정
      const timeUntilWarning = expiryDate.getTime() - Date.now() - SESSION_TIMEOUT_WARNING;
      if (timeUntilWarning > 0) {
        sessionWarningTimeoutRef.current = setTimeout(() => {
          // 세션 만료 경고 표시 (UI에서 처리 가능)
          console.warn('세션이 곧 만료됩니다.');
          // 필요 시 이벤트 디스패치 가능
        }, timeUntilWarning);
      }
    },
    [clearSessionTimeouts, checkSessionStatus],
  );

  // 토큰 갱신 함수
  const refreshToken = useCallback(async (): Promise<void> => {
    // ⛔ 조정자를 쓴다 [부당한 로그아웃 제거 1단계].
    //    종전에는 실패하면 이유를 가리지 않고 `logout()` 을 불렀다 —
    //    서버가 잠깐 흔들린 것만으로 살아 있는 재발급권까지 버렸다.
    const outcome = await requestRefresh();

    if (outcome.kind === 'ok') {
      const 저장된만료 = localStorage.getItem('session_expiry');
      const sessionExpiry = 저장된만료 ? new Date(저장된만료) : new Date(Date.now() + 3600 * 1000);

      dispatch({
        type: AuthActionType.REFRESH_TOKEN_SUCCESS,
        payload: {
          token: outcome.token,
          refreshToken: localStorage.getItem('refresh_token') || undefined,
          sessionExpiry,
        } as RefreshTokenSuccessPayload,
      });

      startSessionTimeoutMonitoring(sessionExpiry);
      return;
    }

    switch (outcome.kind) {
      case 'unavailable':
        // ⛔ 서버 사정이다. **로그아웃하지 않는다.** 던지기만 한다 —
        //    부른 쪽(예: 「연장」 버튼)이 안내를 띄울 수 있게.
        throw new Error('서버에 연결할 수 없습니다. 잠시 후 다시 시도합니다.');

      case 'sms_required':
        // ⛔ **로그아웃하지 않는다.** 전역 재인증 창을 띄우고 던지기만 한다.
        requestSmsReauth({ challenge: outcome.challenge, phoneNumber: outcome.phoneNumber });
        throw new Error('보안을 위해 SMS 인증이 필요합니다.');

      case 'blocked':
        if (typeof window !== 'undefined') {
          const 이미로그인화면 = window.location?.pathname === '/login';
          if (!이미로그인화면) {
            try { window.sessionStorage.setItem('auth_blocked_reason', outcome.reason); } catch { /* noop */ }
          }
        }
        break;

      case 'invalid':
        break;

      default:
        assertNever(outcome);
    }

    dispatch({
      type: AuthActionType.REFRESH_TOKEN_FAILURE,
      payload: '토큰 갱신 실패' as ErrorPayload,
    });

    await logout();
    throw new Error('인증이 만료되었습니다.');
  }, [dispatch, startSessionTimeoutMonitoring, logout]);

  // 세션 연장 함수
  const extendSession = useCallback(async (): Promise<void> => {
    await refreshToken();
  }, [refreshToken]);

  // SMS 인증 성공 시 직접 상태 업데이트 (LoginPage에서 사용)
  const setSmsAuthSuccess = useCallback((response: LoginSuccessPayload): void => {
    // 세션 만료 시간 계산
    const expiresIn = response.expiresIn || 3600; // 기본 1시간
    const sessionExpiry = response.sessionExpiry || new Date(Date.now() + expiresIn * 1000);

    // localStorage에 인증 정보 저장 (페이지 새로고침 시 복원용)
    if (response.token) {
      localStorage.setItem('auth_token', response.token);
    }
    if (response.refreshToken) {
      localStorage.setItem('refresh_token', response.refreshToken);
    }
    if (response.user) {
      localStorage.setItem('user_info', JSON.stringify(response.user));
    }
    localStorage.setItem('session_expiry', sessionExpiry.toISOString());

    // LOGIN_SUCCESS 액션 dispatch
    dispatch({
      type: AuthActionType.LOGIN_SUCCESS,
      payload: {
        user: response.user,
        token: response.token,
        refreshToken: response.refreshToken,
        sessionExpiry,
      } as LoginSuccessPayload,
    });

    // 세션 타임아웃 모니터링 시작
    startSessionTimeoutMonitoring(sessionExpiry);
  }, [dispatch, startSessionTimeoutMonitoring]);

  // 오류 초기화 함수
  const clearError = useCallback((): void => {
    dispatch({ type: AuthActionType.CLEAR_ERROR });
  }, []);

  // 컴포넌트 마운트 시 초기화
  useEffect(() => {
    // 비동기 초기화 함수
    const initAuth = async (): Promise<void> => {
      try{
        // 로컬 스토리지에서 토큰 확인
        const token = localStorage.getItem('auth_token');
        const refreshTokenValue = localStorage.getItem('refresh_token');
        const userInfoString = localStorage.getItem('user_info');
        const sessionExpiryString = localStorage.getItem('session_expiry');

        let userInfo = userInfoString ? JSON.parse(userInfoString) : null;
        const sessionExpiry = sessionExpiryString ? new Date(sessionExpiryString) : null;

        // JWT에서 role을 항상 동기화 (localStorage보다 JWT가 신뢰 소스)
        if (userInfo && token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.role && payload.role !== userInfo.role) {
              userInfo = { ...userInfo, role: payload.role };
              localStorage.setItem('user_info', JSON.stringify(userInfo));
            }
          } catch {}
        }

        // userInfo가 없거나 role이 누락된 경우 서버에서 복구
        //
        // ⛔ 실패가 «서버 사정» 인지 따로 기억한다 — 최종 심사 [F-12].
        //    `/auth/me` 는 재발급 통로가 아니라서, 여기서 5xx 가 나도
        //    조정자의 연결 상태는 여전히 «정상» 이다. 그것만 보고 판단하면
        //    순수 서버 장애가 «다시 로그인하세요» 로 둔갑한다.
        let 서버사정으로복구실패 = false;
        if (token && (!userInfo || !userInfo.role)) {
          try {
            const freshUser = await authService.fetchCurrentUser();
            userInfo = freshUser;
          } catch (fetchError) {
            console.error('AuthContext: /auth/me로 사용자 정보 복구 실패', fetchError);
            서버사정으로복구실패 = isServerSideFailure(fetchError);
          }
        }

        // 토큰이 있는 경우
        if (token) {
          // 세션 만료 확인
          const now = new Date();
          if (sessionExpiry && sessionExpiry > now) {
            // ⛔ 사용자 정보를 끝내 못 구했으면 **인증된 것으로 세우지 않는다** — 최종 심사 [F-10].
            //
            //    종전에는 `user: null` 인 채로 «인증됨» 이 됐다. 그런데 권한 검사는
            //    `state.user` 가 있을 때만 돈다(`ProtectedRoute.tsx:116`). 즉 사용자 정보가
            //    없으면 **역할 관문이 통째로 건너뛰어진다.** 관리자 화면의 유일한 방어선이다.
            //    안전한 쪽으로 닫는다 — 모르면 들여보내지 않는다.
            if (!userInfo) {
              if (서버사정으로복구실패 || getConnectionState() !== 'ok') {
                // 서버 사정이면 로딩을 유지하고 조정자의 재시도를 기다린다 ([F-7]과 같은 처리)
                console.warn('AuthContext: 사용자 정보 복구 보류 — 서버 사정');
              } else {
                dispatch({ type: AuthActionType.LOGIN_FAILURE, payload: null });
              }
              return;
            }

            // 유효한 세션
            dispatch({
              type: AuthActionType.SET_USER,
              payload: {
                user: userInfo,
                token,
                refreshToken: refreshTokenValue,
                sessionExpiry,
              } as SetUserPayload,
            });

            // 세션 타임아웃 모니터링 시작
            startSessionTimeoutMonitoring(sessionExpiry);
          } else if (refreshTokenValue) {
            // 세션 만료되었지만 refreshToken이 있는 경우에만 갱신 시도
            try {
              await refreshToken();
              // 토큰 갱신 성공 시 localStorage의 최신 user 정보를 state에 복원
              // (refreshToken 응답에 user가 포함되므로 authService에서 localStorage 업데이트 완료)
              const refreshedUserInfoStr = localStorage.getItem('user_info');
              const refreshedUserInfo = refreshedUserInfoStr ? JSON.parse(refreshedUserInfoStr) : userInfo;
              if (refreshedUserInfo) {
                const newExpiry = localStorage.getItem('session_expiry');
                const restoredSessionExpiry = newExpiry ? new Date(newExpiry) : null;
                dispatch({
                  type: AuthActionType.SET_USER,
                  payload: {
                    user: refreshedUserInfo,
                    token: localStorage.getItem('auth_token'),
                    refreshToken: localStorage.getItem('refresh_token'),
                    sessionExpiry: restoredSessionExpiry,
                  } as SetUserPayload,
                });
                if (restoredSessionExpiry) {
                  startSessionTimeoutMonitoring(restoredSessionExpiry);
                }
              }
            } catch (refreshError) {
              // ⛔ 여기서 이유를 **가려야 한다** — 최종 심사 [F-2].
              //    종전에는 이유와 무관하게 `LOGIN_FAILURE` 를 보냈고,
              //    그러면 저장소에 토큰이 남아 있어도 **화면은 로그아웃 상태**가 된다.
              //    서버가 잠깐 흔들린 것뿐인데 직원은 로그인 화면을 본다.
              if (getConnectionState() !== 'ok') {
                console.warn('AuthContext: 서버 사정으로 갱신 보류 — 로그아웃하지 않는다', refreshError);
                // 저장돼 있던 것으로 화면을 세워 둔다. 조정자가 뒤에서 다시 시도하고,
                // 성공하면 성공 구독이 새 만료시각으로 감시를 다시 건다.
                if (userInfo) {
                  dispatch({
                    type: AuthActionType.SET_USER,
                    payload: {
                      user: userInfo,
                      token,
                      refreshToken: refreshTokenValue,
                      sessionExpiry,
                    } as SetUserPayload,
                  });
                }
                // ⛔ 사용자 정보가 없으면 **아무것도 보내지 않는다** — 최종 심사 [F-7].
                //    `LOGIN_FAILURE` 를 보내면 «토큰은 있는데 인증 안 됨» 이 되고,
                //    `ProtectedRoute` 가 3초마다 새로고침하는 되돌이에 빠진다
                //    (`ProtectedRoute.tsx:58-70`). 로딩을 유지한 채 조정자의 재시도를 기다린다.
                //    5분을 다 쓰면 아래 연결 상태 구독이 정리한다.
              } else {
                console.error('AuthContext: 토큰 갱신 실패', refreshError);
                dispatch({ type: AuthActionType.LOGIN_FAILURE, payload: null });
              }
            }
          } else {
            // 토큰은 있지만 세션 만료 + refreshToken 없음 → 재로그인 필요
            dispatch({ type: AuthActionType.LOGIN_FAILURE, payload: null });
          }
        } else {
          // 토큰이 없으면 로딩 상태 해제
          dispatch({ type: AuthActionType.LOGIN_FAILURE, payload: null });
        }
      } catch (error) {
        console.error('AuthContext: 초기화 중 오류 발생', error);
        dispatch({
          type: AuthActionType.LOGIN_FAILURE,
          payload: '인증 초기화 실패' as ErrorPayload,
        });
      }
    };

    // 초기화 실행
    initAuth();

    // 컴포넌트 언마운트 시 타임아웃 정리
    return () => {
      clearSessionTimeouts();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 마운트 시 한 번만 실행

  // ── 재발급 성공을 어느 경로에서 했든 화면 시계에 반영한다 ──────────────
  //
  // ⛔ 종전에는 `authService` 경로만 `session_expiry` 를 고쳤다.
  //    axios 가로채기나 fetch 경로로 갱신되면 화면은 **옛 만료시각 그대로** 였고,
  //    새 토큰을 들고도 타이머가 로그아웃시켰다 — 「방금 잘 되다가 갑자기 튕김」의 정체다.
  useEffect(() => {
    return subscribeRefreshSuccess(({ token, expiresAt }) => {
      // 서버가 돌아왔다. 사용자 정보가 있으면 화면을 **완전히** 세운다.
      // ⛔ 초기화가 서버 사정으로 보류된 경우([F-7]) 여기가 유일한 복구 지점이다.
      let 복구된사용자 = null;
      try {
        const raw = localStorage.getItem('user_info');
        복구된사용자 = raw ? JSON.parse(raw) : null;
      } catch { /* 깨져 있으면 아래 갱신만 한다 */ }

      if (복구된사용자) {
        dispatch({
          type: AuthActionType.SET_USER,
          payload: {
            user: 복구된사용자,
            token,
            refreshToken: localStorage.getItem('refresh_token'),
            sessionExpiry: expiresAt,
          } as SetUserPayload,
        });
      } else {
        dispatch({
          type: AuthActionType.REFRESH_TOKEN_SUCCESS,
          payload: {
            token,
            refreshToken: localStorage.getItem('refresh_token') || undefined,
            sessionExpiry: expiresAt,
          } as RefreshTokenSuccessPayload,
        });
      }
      startSessionTimeoutMonitoring(expiresAt);
    });
  }, [dispatch, startSessionTimeoutMonitoring]);

  // ── 5분을 다 써서 포기하면 로딩을 끝낸다 ─────────────────────────────
  //
  // ⛔ 없으면 사용자 정보가 없는 상태에서 **영원히 로딩 화면**에 머문다 — [F-7].
  //    포기 상태에서는 「다시 로그인」 안내가 보여야 하고, 그러려면 로딩이 끝나야 한다.
  useEffect(() => {
    return subscribeConnection((s) => {
      if (s === 'gave-up') {
        dispatch({ type: AuthActionType.LOGIN_FAILURE, payload: null });
      }
    });
  }, [dispatch]);

  // ── 탭 사이 동기화 ──────────────────────────────────────────────────
  //
  // ⛔ 네 열쇠를 **한 벌로** 본다. `session_expiry` 만 들으면 두 가지가 깨진다.
  //    ① 두 번째 탭에서 **다른 사람이 로그인**하면 첫 번째 탭이 그대로 남아
  //       **남의 데이터를 보게 된다.** 이건 편의가 아니라 안전 문제다.
  //    ② `clearAuthAndRedirect()` 로 나간 로그아웃이 다른 탭에 **전파되지 않는다.**
  //
  // ⛔ `storage` 이벤트는 **쓴 탭 자신에게는 오지 않는다.** 자기 탭은 위 구독이 처리한다.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const 관심열쇠 = ['auth_token', 'refresh_token', 'user_info', 'session_expiry'];

    const 사용자번호 = (jwt: string | null): string | null => {
      if (!jwt) return null;
      try {
        return JSON.parse(atob(jwt.split('.')[1]))?.id ?? null;
      } catch {
        return null;
      }
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && !관심열쇠.includes(e.key)) return;

      const 새토큰 = localStorage.getItem('auth_token');

      // ① 토큰이 사라졌다 = 다른 탭에서 로그아웃했다 → 이 탭도 나간다
      if (!새토큰) {
        if (state.isAuthenticated) void logout();
        return;
      }

      // ② 사용자가 바뀌었다 → 이 탭은 옛 사용자 화면이다
      //
      // ⛔ 여기서 `logout()` 을 부르면 **안 된다** — 최종 심사 [F-6].
      //    `logout()` 은 localStorage 를 지우는데, 그 저장소는 **모든 탭이 함께 쓴다.**
      //    그러면 방금 로그인한 새 사용자의 토큰까지 지워지고,
      //    그 탭이 다시 이 탭에게 «토큰이 사라졌다» 를 알려 **서로 밀어내는 되돌이**가 된다.
      //
      //    이 탭만 새 사용자 기준으로 다시 세우면 된다. 그것이 새로고침이다.
      const 지금사용자 = state.user?.id ? String(state.user.id) : null;
      const 새사용자 = 사용자번호(새토큰);
      if (지금사용자 && 새사용자 && 지금사용자 !== 새사용자) {
        clearSessionTimeouts();
        if (typeof window !== 'undefined') window.location.reload();
        return;
      }

      // ③ 같은 사용자의 갱신 → 새 만료시각을 따른다
      const 저장된만료 = localStorage.getItem('session_expiry');
      if (!저장된만료) return;
      const 새만료 = new Date(저장된만료);
      if (Number.isNaN(새만료.getTime())) return;

      // ⛔ 같은 값이면 아무것도 하지 않는다 — 되돌아오는 루프를 만들지 않는다
      const 지금만료 = state.sessionExpiry ? new Date(state.sessionExpiry).getTime() : 0;
      if (새만료.getTime() === 지금만료) return;

      dispatch({
        type: AuthActionType.REFRESH_TOKEN_SUCCESS,
        payload: {
          token: 새토큰,
          refreshToken: localStorage.getItem('refresh_token') || undefined,
          sessionExpiry: 새만료,
        } as RefreshTokenSuccessPayload,
      });
      startSessionTimeoutMonitoring(새만료);
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [state.isAuthenticated, state.user, state.sessionExpiry, logout, dispatch, startSessionTimeoutMonitoring]);

  // 컨텍스트 값
  const contextValue: AuthContextProps = {
    state,
    login,
    register,
    logout,
    refreshToken,
    clearError,
    extendSession,
    setSmsAuthSuccess,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// 인증 컨텍스트 사용 훅
export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
